import React from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import NotificationsIcon from '@mui/icons-material/Notifications';
import CurrencyBitcoinIcon from '@mui/icons-material/CurrencyBitcoin';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { useNavigate } from 'react-router-dom';

const FeatureCard = ({ icon, title, steps, color = '#4f46e5', onClick }) => (
  <Card 
    onClick={onClick}
    elevation={0} 
    sx={{ 
      height: '100%', 
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'divider',
      borderRadius: 3,
      transition: 'transform 0.2s, box-shadow 0.2s, cursor 0.2s',
      cursor: onClick ? 'pointer' : 'default',
      '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: 4,
      }
    }}
  >
    <CardContent sx={{ p: 3 }}>
      <Box 
        sx={{ 
          display: 'inline-flex', 
          p: 1.5, 
          borderRadius: 2, 
          bgcolor: `${color}15`,
          color: color,
          mb: 2 
        }}
      >
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {title}
      </Typography>
      <Box component="ol" sx={{ pl: 2.5, m: 0 }}>
        {steps.map((step, i) => (
          <Typography 
            component="li" 
            key={i} 
            sx={{ 
              mb: 1, 
              color: 'text.secondary',
              fontSize: '0.95rem',
              lineHeight: 1.6
            }}
          >
            {step}
          </Typography>
        ))}
      </Box>
    </CardContent>
  </Card>
);

const QuickTip = ({ emoji, text }) => (
  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}>
    <Typography sx={{ fontSize: '1.2rem' }}>{emoji}</Typography>
    <Typography color="text.secondary" sx={{ fontSize: '0.95rem' }}>{text}</Typography>
  </Box>
);

const VisualizationGuide = () => {
  const navigate = useNavigate();
  return (
    <Box sx={{ p: { xs: 2, md: 4 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Header */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 4, 
          mb: 4, 
          borderRadius: 4,
          background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 100%)',
          color: 'white',
          textAlign: 'center'
        }}
      >
        <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
          Welcome to tickers.fyi
        </Typography>
        <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
          Your personal market analysis dashboard
        </Typography>
        <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
          <Chip label="Subscription plans" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          <Chip label="Real financial data" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
          <Chip label="Account required" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }} />
        </Box>
      </Paper>

      {/* Stock market explainer video */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 4,
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
          Stock Market Video
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          How the Stock Market Works
        </Typography>
        <Box
          sx={{
            position: 'relative',
            width: '100%',
            pt: '56.25%',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: '#000',
          }}
        >
          <Box
            component="iframe"
            src="https://www.youtube.com/embed/OQrs6Oa8qtE?autoplay=1&mute=1&playsinline=1&rel=0"
            title="How the Stock Market Works"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              border: 0,
            }}
          />
        </Box>
      </Paper>

      {/* Main Features */}
      <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
        What You Can Do
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 5 }}>
        <Grid item xs={12} sm={6} md={4}>
          <FeatureCard
            icon={<ShowChartIcon fontSize="large" />}
            title="Stocks"
            color="#10b981"
            onClick={() => navigate('/dashboard')}
            steps={[
              'Open stock dashboard views',
              'Jump into company financials',
              'Analyze individual tickers quickly'
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FeatureCard
            icon={<TableChartIcon fontSize="large" />}
            title="Rates"
            color="#ec4899"
            onClick={() => navigate('/fed-rates')}
            steps={[
              'Track Fed policy rates',
              'Review historical rate movements',
              'Monitor short and long yields'
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FeatureCard
            icon={<CalendarTodayIcon fontSize="large" />}
            title="Economic Data"
            color="#3b82f6"
            onClick={() => navigate('/economic-calendar')}
            steps={[
              'View upcoming economic releases',
              'Check latest macro indicators',
              'Stay ahead of key market events'
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FeatureCard
            icon={<NotificationsIcon fontSize="large" />}
            title="Earnings Calender"
            color="#0ea5e9"
            onClick={() => navigate('/earnings-calendar')}
            steps={[
              'See upcoming earnings dates',
              'Find companies reporting soon',
              'Jump from calendar to analysis'
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FeatureCard
            icon={<CurrencyBitcoinIcon fontSize="large" />}
            title="Crypto"
            color="#ef4444"
            onClick={() => navigate('/crypto-analysis')}
            steps={[
              'Top 100 cryptocurrencies',
              'Market cap treemap',
              'On-chain metrics & volume'
            ]}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <FeatureCard
            icon={<AccountTreeIcon fontSize="large" />}
            title="Sankey Diagrams"
            color="#8b5cf6"
            onClick={() => navigate('/financial-statements')}
            steps={[
              'See how revenue flows to profit',
              'Visualize costs and operating leverage',
              'Compare period-by-period flow changes'
            ]}
          />
        </Grid>
      </Grid>

      {/* Tips */}
      <Paper 
        elevation={0} 
        sx={{ 
          p: 3, 
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Pro Tips
        </Typography>
        <QuickTip emoji="💡" text="Your selections are saved — come back anytime and pick up where you left off" />
        <QuickTip emoji="🌙" text="Toggle dark/light mode with the button in the header" />
        <QuickTip emoji="📊" text="Hover over treemap cells to see details for small tickers" />
        <QuickTip emoji="⏱️" text="Change time periods in Financial Statements to see quarterly trends" />
        <QuickTip emoji="🔄" text="Refresh data before comparing — ensures you're seeing the latest numbers" />
      </Paper>

    </Box>
  );
};

export default VisualizationGuide;
